import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Typography,
  Box,
} from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired, getContainerStyles } from '../../utils/formUtils';

interface WorkHistoryProps {
  data?: any;
  onChange?: (data: any) => void;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface FormData {
  // Employment when Injured
  employment_when_injured: string;
  date_started: string;
  date_stopped: string;
  employer_name: string;
  employer_city_state: string;
  job_title: string;
  job_description: string;

  // Job Details
  time_to_learn_job: string;
  union_name_local: string;
  number_people_supervised: string;

  // Shifts
  shift_1st: boolean;
  shift_2nd: boolean;
  shift_3rd: boolean;
  shift_rotating: boolean;

  // Machinery and Physical Demands
  machinery_operated: string;
  hours_sitting: string;
  hours_standing: string;
  hours_walking: string;
  weight_lifted_pounds: string;
  items_lifted: string;

  // Salary and Hours
  salary_amount: string;
  salary_frequency: string; // weekly, monthly, yearly
  hours_per_day: string;
  overtime_opportunities: string;
  overtime_hours_per_week: string;

  // Reason for Leaving
  reason_for_leaving: string;
}

const WorkHistory: React.FC<WorkHistoryProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false,
}) => {
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => { /* noop */ });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'work_history', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'work_history', fieldMappings);
  const [formData, setFormData] = useState<FormData>({
    // Employment when Injured
    employment_when_injured: '',
    date_started: '',
    date_stopped: '',
    employer_name: '',
    employer_city_state: '',
    job_title: '',
    job_description: '',

    // Job Details
    time_to_learn_job: '',
    union_name_local: '',
    number_people_supervised: '',

    // Shifts
    shift_1st: false,
    shift_2nd: false,
    shift_3rd: false,
    shift_rotating: false,

    // Machinery and Physical Demands
    machinery_operated: '',
    hours_sitting: '',
    hours_standing: '',
    hours_walking: '',
    weight_lifted_pounds: '',
    items_lifted: '',

    // Salary and Hours
    salary_amount: '',
    salary_frequency: '',
    hours_per_day: '',
    overtime_opportunities: '',
    overtime_hours_per_week: '',

    // Reason for Leaving
    reason_for_leaving: '',
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: FormData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Work History
        {readOnly && (
          <Typography
            component='span'
            variant='body2'
            color='warning.main'
            sx={{ ml: 2, fontStyle: 'italic', fontWeight: 'bold' }}
          >
            (Read Only - View Only)
          </Typography>
        )}
      </Typography>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 3,
        ...getContainerStyles(readOnly)
      }}>
        {/* Employment when Injured */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Employment when Injured
          </Typography>
          <TextField
            fullWidth
            label='Employment when Injured'
            value={formData.employment_when_injured}
            onChange={e => handleChange('employment_when_injured', e.target.value)}
            required={isFieldRequiredLocal('employment_when_injured')}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label='Date Started (Specify Year)'
              type='date'
              value={formData.date_started}
              onChange={e => handleChange('date_started', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <TextField
              label='Date Stopped (Specify Year)'
              type='date'
              value={formData.date_stopped}
              onChange={e => handleChange('date_stopped', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>
        </Box>

        {/* Employer Information */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Employer Information
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label='Employer Name'
              value={formData.employer_name}
              onChange={e => handleChange('employer_name', e.target.value)}
              required={isFieldRequiredLocal('employer_name')}
            />
            <TextField
              fullWidth
              label='City & State'
              value={formData.employer_city_state}
              onChange={e => handleChange('employer_city_state', e.target.value)}
              required={isFieldRequiredLocal('employer_city_state')}
            />
            <TextField
              fullWidth
              label='Job Title'
              value={formData.job_title}
              onChange={e => handleChange('job_title', e.target.value)}
              required={isFieldRequiredLocal('job_title')}
            />
            <TextField
              fullWidth
              label='Job Description (Describe in Detail & Attach any necessary documents relevant)'
              multiline
              rows={4}
              value={formData.job_description}
              onChange={e => handleChange('job_description', e.target.value)}
            />
          </Box>
        </Box>

        {/* Job Details */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Job Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label='How Long did it take you to learn the Job?'
              value={formData.time_to_learn_job}
              onChange={e => handleChange('time_to_learn_job', e.target.value)}
            />
            <TextField
              fullWidth
              label='Union? (Provide Name and Local)'
              value={formData.union_name_local}
              onChange={e => handleChange('union_name_local', e.target.value)}
            />
            <TextField
              fullWidth
              label='Number of people Supervised?'
              value={formData.number_people_supervised}
              onChange={e =>
                handleChange('number_people_supervised', e.target.value)
              }
            />
          </Box>
        </Box>

        {/* Shifts */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Please specify the shift while on the job
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.shift_1st}
                  onChange={e => handleChange('shift_1st', e.target.checked)}
                />
              }
              label='1st Shift'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.shift_2nd}
                  onChange={e => handleChange('shift_2nd', e.target.checked)}
                />
              }
              label='2nd Shift'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.shift_3rd}
                  onChange={e => handleChange('shift_3rd', e.target.checked)}
                />
              }
              label='3rd Shift'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.shift_rotating}
                  onChange={e =>
                    handleChange('shift_rotating', e.target.checked)
                  }
                />
              }
              label='Rotating/Other'
            />
          </Box>
        </Box>

        {/* Machinery */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Machinery
          </Typography>
          <TextField
            fullWidth
            label='Did you Operate any Machinery? (Type)'
            value={formData.machinery_operated}
            onChange={e => handleChange('machinery_operated', e.target.value)}
          />
        </Box>

        {/* Physical Demands */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Physical Demands
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label='How Many Hours Did you Sit on the Job?'
              value={formData.hours_sitting}
              onChange={e => handleChange('hours_sitting', e.target.value)}
            />
            <TextField
              fullWidth
              label='How Many Hours Did you Stand on the Job?'
              value={formData.hours_standing}
              onChange={e => handleChange('hours_standing', e.target.value)}
            />
            <TextField
              fullWidth
              label='How Many Hours Did you have to Walk on the Job?'
              value={formData.hours_walking}
              onChange={e => handleChange('hours_walking', e.target.value)}
            />
            <TextField
              fullWidth
              label='How much weight did you lift on the job in pounds?'
              value={formData.weight_lifted_pounds}
              onChange={e =>
                handleChange('weight_lifted_pounds', e.target.value)
              }
            />
            <TextField
              fullWidth
              label='What did you lift? (Materials, Products, Items etc)'
              multiline
              rows={3}
              value={formData.items_lifted}
              onChange={e => handleChange('items_lifted', e.target.value)}
            />
          </Box>
        </Box>

        {/* Salary and Hours */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Salary and Hours
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Typography variant='subtitle1'>Gross Salary?</Typography>
              <FormControl component='fieldset'>
                <RadioGroup
                  value={formData.salary_frequency}
                  onChange={e =>
                    handleChange('salary_frequency', e.target.value)
                  }
                  sx={{ flexDirection: 'row' }}
                >
                  <FormControlLabel
                    value='weekly'
                    control={<Radio />}
                    label='Weekly'
                  />
                  <FormControlLabel
                    value='monthly'
                    control={<Radio />}
                    label='Monthly'
                  />
                  <FormControlLabel
                    value='yearly'
                    control={<Radio />}
                    label='Yearly'
                  />
                </RadioGroup>
              </FormControl>
              <TextField
                label='$'
                value={formData.salary_amount}
                onChange={e => handleChange('salary_amount', e.target.value)}
                sx={{ width: 150 }}
              />
            </Box>

            <TextField
              fullWidth
              label='How many hours a day did you work?'
              value={formData.hours_per_day}
              onChange={e => handleChange('hours_per_day', e.target.value)}
            />

            <Box>
              <Typography variant='subtitle1' gutterBottom>
                Were there overtime Opportunities?
              </Typography>
              <FormControl component='fieldset' sx={{ mb: 2 }}>
                <RadioGroup
                  value={formData.overtime_opportunities}
                  onChange={e =>
                    handleChange('overtime_opportunities', e.target.value)
                  }
                >
                  <FormControlLabel
                    value='yes'
                    control={<Radio />}
                    label='Yes'
                  />
                  <FormControlLabel value='no' control={<Radio />} label='No' />
                </RadioGroup>
              </FormControl>
              {formData.overtime_opportunities === 'yes' && (
                <TextField
                  fullWidth
                  label='(If Yes, How many hours a week)'
                  value={formData.overtime_hours_per_week}
                  onChange={e =>
                    handleChange('overtime_hours_per_week', e.target.value)
                  }
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Reason for Leaving */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Reason for Leaving
          </Typography>
          <TextField
            fullWidth
            label='Reason for Leaving?'
            multiline
            rows={3}
            value={formData.reason_for_leaving}
            onChange={e => handleChange('reason_for_leaving', e.target.value)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default WorkHistory;
